import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../BlocksElement';
import '../skeleton/skeleton';
import type { BadgeColor, BadgePosition, BadgeSize } from './badge.model';
import styles from './badge.styles';

/**
 * @slot before - The Badge before
 * @csspart base - The component's base wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart part-text - The component's text.
 * @csspart badge-text - The component's  badge text.
 * @csspart label - The component's label.
 * @csspart skeleton - The component's skeleton wrapper.
 * @csspart loader - The component's loader wrapper for isDot.
 */
export class BlocksBadge extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * The badge text
   */
  @property({ type: String, reflect: true })
  text: string | undefined;

  /**
   * Loading state
   * 1) Renders badge as a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * isDot boolean
   */
  @property({ type: Boolean })
  isDot: boolean | undefined;

  /**
   * Badge variant
   */
  @property({ type: String, reflect: true })
  variant: BadgeColor = 'blue';

  /**
   * Size variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**sm** decreases overall size for badge with 16px height</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: BadgeSize = 'md';

  /**
   * Positions the badge absolutely to the parent container.
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**top-left** places the badge to the top left</li>
   * <li>**top-right** places the badge to the top right</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  position: BadgePosition;

  /**
   * Aria label for the link for accessibility
   * 1) This should contain a readout of the data the badge is representing.
   */
  @property({ type: String, reflect: true })
  arialabel: string | undefined;

  /**
   * First updated lifecycle hook
   * 1) Handle the position of the parent element
   */
  firstUpdated() {
    this.handlePosition();
  }

  /**
   * Handle position
   * 1) If a position is defined, then apply position: relative to the direct parent element
   */
  private handlePosition() {
    if (this.position && this.parentElement) {
      this.parentElement.style.position = 'relative';
      this.parentElement.style.width = 'fit-content';
    }
  }

  private renderLoader(componentClassNames: DirectiveResult<typeof ClassMapDirective>): TemplateResult<1> {
    return html`
      <div part="base" class="${componentClassNames}">
        ${this.size === 'sm'
          ? html` <cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} size="sm" width="39px"></cat-skeleton> `
          : html`<cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} width="44px"></cat-skeleton>`}
      </div>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-badge', {
      'cat-c-badge--blue': this.variant === 'blue' && !this.isLoading,
      'cat-c-badge--green': this.variant === 'green',
      'cat-c-badge--grey': this.variant === 'grey',
      'cat-c-badge--yellow': this.variant === 'yellow',
      'cat-c-badge--brand': this.variant === 'brand',
      'cat-c-badge--orange': this.variant === 'orange',
      'cat-c-badge--red': this.variant === 'red',
      'cat-c-badge--contrast': this.variant === 'contrast',
      'cat-c-badge--disabled': this.variant === 'disabled',
      'cat-is-dot': this.isDot,
      'cat-c-badge--sm': this.size === 'sm',
      'cat-c-badge--top-left': this.position === 'top-left',
      'cat-c-badge--top-right': this.position === 'top-right',
      'cat-is-loading': this.isLoading
    });
    if (this.isLoading && !this.isDot) {
      return this.renderLoader(componentClassNames);
    }
    return html`
      <div part="base" class="${componentClassNames}" aria-label="${ifDefined(this.arialabel)}">
        ${html`<slot part="part-before" name="before"></slot>`}
        ${!this.isDot && this.text ? html`<span part="badge-text" class="cat-c-text">${this.text}</span>` : nothing}
      </div>
      ${this.isDot && this.isLoading
        ? html`<span part="loader" class="cat-dot-loader"
            ><cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} size="sm" width="39px"></cat-skeleton
          ></span>`
        : this.isDot && this.text
          ? html`<span part="label" class="cat-dot-optional-label">${this.text}</span>`
          : nothing}
    `;
  }
}

if (customElements.get('cat-badge') === undefined) {
  customElements.define('cat-badge', BlocksBadge);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-badge': BlocksBadge;
  }
}
